import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaCheckCircle,
  FaCompass,
  FaExternalLinkAlt,
  FaFeatherAlt,
  FaFlag,
  FaHeart,
  FaImage,
  FaLink,
  FaPaperPlane,
  FaRegHeart,
  FaSpinner,
  FaStar,
  FaTimes,
  FaTrashAlt,
  FaUsers,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { socialService, userService } from '../../services/apiService';
import SocialNav from '../../components/SocialNav';
import PostContentWithTranslate from '../../components/PostTranslateControl';
import MentionTextarea from '../../components/MentionTextarea';
import ReportModal from '../../components/ReportModal';
import { formatRelative } from '../../i18n/formatRelative';
import '../UserProfile/UserProfile.css';
import '../UserProfile/SocialPremium.css';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

export default function Feed() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusedPostId, setFocusedPostId] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [highlighted, setHighlighted] = useState(false);
  const [posts, setPosts] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [likingId, setLikingId] = useState(null);
  const [burstId, setBurstId] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const isSystemAccount = Boolean(user?.systemAccount?.enabled);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [feedRes, suggestedRes] = await Promise.all([
        socialService.getFeed({ limit: 30 }),
        userService.getSuggestedProfiles(5),
      ]);
      setPosts(feedRes.data.posts || []);
      setSuggested(suggestedRes.data.profiles || []);
    } catch (err) {
      setError(err.message || t('feed.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    const postId = searchParams.get('post');
    if (!postId || loading) return undefined;

    const timer = window.setTimeout(() => {
      const element = document.getElementById(`post-${postId}`);
      if (element) {
        setFocusedPostId(postId);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => setFocusedPostId(''), 3200);
      }

      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('post');
        return next;
      }, { replace: true });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [loading, posts, searchParams, setSearchParams]);

  const handlePublish = async (event) => {
    event.preventDefault();
    const text = content.trim();
    if (!text && !image && !linkUrl.trim()) return;

    setPublishing(true);
    setStatus(null);
    try {
      const payload = {
        content: text,
        highlighted: isSystemAccount ? highlighted : false,
      };
      if (isSystemAccount) {
        if (image) payload.image = image;
        if (imageAlt.trim()) payload.imageAlt = imageAlt.trim();
        if (linkUrl.trim()) {
          payload.linkUrl = linkUrl.trim();
          if (linkTitle.trim()) payload.linkTitle = linkTitle.trim();
          if (linkDescription.trim()) payload.linkDescription = linkDescription.trim();
        }
      }
      const res = await socialService.createPost(payload);
      setPosts((current) => [res.data.post, ...current]);
      setContent('');
      setImage(null);
      setImagePreview('');
      setImageAlt('');
      setLinkUrl('');
      setLinkTitle('');
      setLinkDescription('');
      setHighlighted(false);
      setStatus({ type: 'success', message: t('feed.published') });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setPublishing(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', message: t('feed.imageTooLarge') });
      event.target.value = '';
      return;
    }
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview('');
    setImageAlt('');
  };

  const handleDelete = async (postId) => {
    try {
      await socialService.deletePost(postId);
      setPosts((current) => current.filter((post) => post.id !== postId));
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    }
  };

  const handleReportPost = (postId) => {
    setReportTarget({ type: 'post', id: postId });
  };

  const handleLike = async (post) => {
    if (likingId) return;
    const previous = { likedByMe: post.likedByMe, likesCount: post.likesCount };
    const nextLiked = !post.likedByMe;
    setLikingId(post.id);
    setPosts((current) => current.map((item) => (
      item.id === post.id
        ? {
          ...item,
          likedByMe: nextLiked,
          likesCount: Math.max(0, (Number(item.likesCount) || 0) + (nextLiked ? 1 : -1)),
        }
        : item
    )));

    if (nextLiked) {
      setBurstId(post.id);
      window.setTimeout(() => {
        setBurstId((current) => (current === post.id ? null : current));
      }, 650);
    }

    try {
      const res = await socialService.toggleLike(post.id);
      setPosts((current) => current.map((item) => (
        item.id === post.id
          ? {
            ...item,
            likedByMe: Boolean(res.data?.liked),
            likesCount: Number(res.data?.likesCount) || 0,
          }
          : item
      )));
    } catch (err) {
      setPosts((current) => current.map((item) => (
        item.id === post.id
          ? { ...item, likedByMe: previous.likedByMe, likesCount: previous.likesCount }
          : item
      )));
      setStatus({ type: 'error', message: err.message });
    } finally {
      setLikingId(null);
    }
  };

  const displayName = user?.profile?.displayName || user?.username || '';

  return (
    <main className="social-page feed-page">
      <div className="social-orb social-orb-a" aria-hidden="true" />
      <div className="social-orb social-orb-b" aria-hidden="true" />
      <SocialNav username={user?.username} />

      <div className="social-shell">
        <header className="social-page-heading feed-heading">
          <div>
            <span className="social-page-kicker"><FaFeatherAlt /> {t('feed.kicker')}</span>
            <h1>{t('feed.title')}</h1>
            <p>{t('feed.subtitle')}</p>
          </div>
          <div className="feed-heading-badge">
            <span className="feed-live-dot" />
            <span><strong>{posts.length}</strong> {t('feed.updatesLabel')}</span>
          </div>
        </header>

        {status && <div className={`social-alert ${status.type}`}>{status.message}</div>}

        <div className="feed-layout">
          <div className="feed-main">
            <section className="social-card feed-composer">
              <div className="feed-composer-inner">
                <Link to={`/perfil/${user?.username || ''}`} className="social-avatar composer-avatar" aria-label={t('feed.openMyProfile')}>
                  {user?.avatarUrl
                    ? <img src={user.avatarUrl} alt="" />
                    : getInitials(displayName)}
                </Link>
                <div className="feed-composer-content">
                  <div className="feed-composer-title">
                    <div>
                      <strong>{t('feed.composerTitle')}</strong>
                      <span>{isSystemAccount ? t('feed.composerHintSystem') : t('feed.composerHint')}</span>
                    </div>
                    <FaFeatherAlt aria-hidden="true" />
                  </div>
                  <form onSubmit={handlePublish}>
                    <MentionTextarea
                      value={content}
                      onChange={(nextValue) => setContent(nextValue.slice(0, isSystemAccount ? 1500 : 500))}
                      placeholder={t('feed.placeholder', { name: displayName })}
                      maxLength={isSystemAccount ? 1500 : 500}
                      disabled={publishing}
                    />
                    {isSystemAccount && (
                      <div className="institutional-post-tools">
                        <div className="institutional-post-tool-row">
                          <label className="institutional-upload-btn">
                            <FaImage aria-hidden="true" />
                            <span>{image ? t('feed.changeImage') : t('feed.addImage')}</span>
                            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageSelect} />
                          </label>
                          <span className="institutional-tool-note">{t('feed.imageFormats')}</span>
                        </div>

                        {imagePreview && (
                          <div className="institutional-image-preview">
                            <img src={imagePreview} alt="" />
                            <button type="button" onClick={clearImage} aria-label={t('feed.removeImage')}><FaTimes /></button>
                            <input
                              value={imageAlt}
                              onChange={(event) => setImageAlt(event.target.value.slice(0, 180))}
                              placeholder={t('feed.imageAltPlaceholder')}
                              maxLength={180}
                            />
                          </div>
                        )}

                        <div className="institutional-link-fields">
                          <label>
                            <FaLink aria-hidden="true" />
                            <input
                              type="url"
                              value={linkUrl}
                              onChange={(event) => setLinkUrl(event.target.value)}
                              placeholder={t('feed.linkPlaceholder')}
                            />
                          </label>
                          {linkUrl.trim() && (
                            <>
                              <input
                                value={linkTitle}
                                onChange={(event) => setLinkTitle(event.target.value.slice(0, 120))}
                                placeholder={t('feed.linkTitlePlaceholder')}
                                maxLength={120}
                              />
                              <input
                                value={linkDescription}
                                onChange={(event) => setLinkDescription(event.target.value.slice(0, 280))}
                                placeholder={t('feed.linkDescPlaceholder')}
                                maxLength={280}
                              />
                            </>
                          )}
                        </div>
                        <label className="institutional-highlight-toggle">
                          <input type="checkbox" checked={highlighted} onChange={(event) => setHighlighted(event.target.checked)} />
                          <span />
                          {t('feed.highlight')}
                        </label>
                      </div>
                    )}
                    <div className="feed-composer-footer">
                      <span className={content.length > (isSystemAccount ? 1350 : 450) ? 'is-near-limit' : ''}>{content.length}/{isSystemAccount ? 1500 : 500}</span>
                      <button type="submit" className="social-btn" disabled={publishing || (!content.trim() && !image && !linkUrl.trim())}>
                        {publishing ? <FaSpinner className="social-spin" /> : <FaPaperPlane />}
                        {t('feed.publish')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </section>

            <section className="social-card feed-stream">
              <div className="feed-stream-head">
                <div>
                  <span className="social-card-eyebrow">{t('feed.timeline')}</span>
                  <h2>{t('feed.forYou')}</h2>
                </div>
                <span className="feed-stream-filter"><span /> {t('feed.mostRecent')}</span>
              </div>
              {loading ? (
                <div className="social-state compact">
                  <FaSpinner className="social-spin" />
                </div>
              ) : error ? (
                <p className="social-empty">{error}</p>
              ) : posts.length === 0 ? (
                <div className="social-empty-state feed-empty">
                  <span className="social-empty-icon"><FaUsers /></span>
                  <strong>{t('feed.emptyTitle')}</strong>
                  <p>{t('feed.emptyText')}</p>
                  <Link to="/explorar" className="social-btn social-btn-ghost">
                    <FaCompass /> {t('feed.exploreCommunity')}
                  </Link>
                </div>
              ) : (
                <ul className="social-post-list">
                  {posts.map((post) => {
                    const authorName = post.author?.profile?.displayName || post.author?.username || t('common.user');
                    const canDelete = user?.role === 'admin'
                      || post.author?.id === user?.id
                      || post.author?.username === user?.username;
                    return (
                      <li
                        id={`post-${post.id}`}
                        key={post.id}
                        className={`social-post${post.highlighted ? ' is-highlighted' : ''}${focusedPostId === post.id ? ' is-focused' : ''}`}
                      >
                        <div className="social-post-head">
                          <Link to={`/perfil/${post.author?.username}`} className="social-post-author-block">
                            <span className="social-avatar post-avatar" aria-hidden="true">
                              {post.author?.avatarUrl
                                ? <img src={post.author.avatarUrl} alt="" />
                                : getInitials(authorName)}
                            </span>
                            <span>
                              <strong>
                                {authorName}
                                {post.author?.verified && <FaCheckCircle className="post-verified" />}
                              </strong>
                              <small>@{post.author?.username} · {formatRelative(post.createdAt, t)}</small>
                            </span>
                          </Link>
                          {canDelete && (
                            <button
                              type="button"
                              className="social-icon-btn"
                              aria-label={t('feed.deleteAria')}
                              onClick={() => handleDelete(post.id)}
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                          {!canDelete && (
                            <button
                              type="button"
                              className="social-icon-btn"
                              aria-label={t('feed.reportAria')}
                              title={t('feed.reportAria')}
                              onClick={() => handleReportPost(post.id)}
                            >
                              <FaFlag />
                            </button>
                          )}
                        </div>
                        {post.highlighted && (
                          <span className="institutional-post-badge"><FaStar aria-hidden="true" /> {t('feed.highlightBadge')}</span>
                        )}
                        <PostContentWithTranslate post={post} />
                        {post.media?.url && (
                          <figure className="social-post-media">
                            <img
                              src={post.media.url}
                              alt={post.media.alt || ''}
                              loading="lazy"
                              width={post.media.width || undefined}
                              height={post.media.height || undefined}
                            />
                          </figure>
                        )}
                        {post.linkPreview?.url && (
                          <a className="social-link-preview" href={post.linkPreview.url} target="_blank" rel="noopener noreferrer">
                            <span className="social-link-preview-icon"><FaLink /></span>
                            <span>
                              <small>{post.linkPreview.host}</small>
                              <strong>{post.linkPreview.title || post.linkPreview.host}</strong>
                              {post.linkPreview.description && <em>{post.linkPreview.description}</em>}
                            </span>
                            <FaExternalLinkAlt className="social-link-preview-external" />
                          </a>
                        )}
                        <div className="social-post-actions">
                          <button
                            type="button"
                            className={`social-like-btn${post.likedByMe ? ' is-liked' : ''}${burstId === post.id ? ' is-bursting' : ''}`}
                            aria-label={post.likedByMe ? t('feed.unlikeAria') : t('feed.likeAria')}
                            aria-pressed={Boolean(post.likedByMe)}
                            disabled={likingId === post.id}
                            onClick={() => handleLike(post)}
                          >
                            <span className="social-like-icon" aria-hidden="true">
                              {post.likedByMe ? <FaHeart /> : <FaRegHeart />}
                              <span className="social-like-ring" />
                              <span className="social-like-spark social-like-spark-a" />
                              <span className="social-like-spark social-like-spark-b" />
                              <span className="social-like-spark social-like-spark-c" />
                              <span className="social-like-spark social-like-spark-d" />
                            </span>
                            <span className="social-like-count">{Number(post.likesCount) || 0}</span>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>

          <aside className="feed-side">
            <section className="social-card feed-discovery-card">
              <div className="social-card-head">
                <div>
                  <span className="social-card-eyebrow">{t('feed.discoveryEyebrow')}</span>
                  <h2>{t('feed.discoveryTitle')}</h2>
                </div>
                <FaUsers className="feed-side-head-icon" />
              </div>
              {suggested.length === 0 ? (
                <p className="social-empty">{t('feed.noSuggestions')}</p>
              ) : (
                <ul className="social-user-list">
                  {suggested.map((person) => {
                    const name = person.profile?.displayName || person.username;
                    return (
                      <li key={person.id}>
                        <Link to={`/perfil/${person.username}`} className="social-user-row">
                          <span className="social-avatar md" aria-hidden="true">
                            {person.avatarUrl ? <img src={person.avatarUrl} alt="" /> : getInitials(name)}
                          </span>
                          <span className="social-user-copy">
                            <strong>{name}{person.verified && <FaCheckCircle className="post-verified" />}</strong>
                            <small>@{person.username}</small>
                            <em>{person.profile?.bio || t('common.memberOfVihs')}</em>
                          </span>
                          <FaArrowRight className="social-user-chevron" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Link to="/explorar" className="feed-discovery-more">
                {t('feed.seeAllCommunity')} <FaArrowRight />
              </Link>
            </section>

            <section className="feed-tip-card">
              <span className="feed-tip-icon"><FaFeatherAlt /></span>
              <div>
                <strong>{t('feed.tipTitle')}</strong>
                <p>{t('feed.tipText')}</p>
              </div>
            </section>
          </aside>
        </div>
      </div>

      <ReportModal
        open={Boolean(reportTarget)}
        targetType={reportTarget?.type}
        targetId={reportTarget?.id}
        title={t('feed.reportTitle')}
        description={t('feed.reportDescription')}
        onClose={() => setReportTarget(null)}
        onSuccess={(message) => setStatus({ type: 'success', message })}
      />
    </main>
  );
}
